/**
 * Screenshot / Export utility
 *
 * Uses html-to-image (SVG foreignObject) to capture the page.
 * Unlike html2canvas, html-to-image lets the **browser** render
 * all CSS natively — so modern colour functions like oklch(),
 * oklab(), lab(), lch(), color() all work out of the box.
 *
 * Two capture modes:
 *   - Viewport: captures exactly what's visible on screen
 *   - Full page: captures the entire scrollable page content
 */

import { toBlob } from "html-to-image";

export interface ScreenshotOptions {
  target?: HTMLElement | null;
  filenamePrefix?: string;
  scale?: number;
  hideEditUI?: boolean;
}

/* ── selectors for UI elements to hide during capture ── */

const HIDE_SELECTORS = [
  "[data-edit-toolbar]",
  "[data-comment-pin]",
  "[data-comment-panel]",
  "[data-block-palette]",
  "[data-admin-bar]",
  "[data-screenshot-modal]",
  ".fixed.bottom-24.left-6",
  ".fixed.bottom-24",
  ".fixed.bottom-6.left-6",
  ".fixed.bottom-6.right-6",
  ".fixed.bottom-6",
];

function hideEditUIElements(): HTMLElement[] {
  const hidden: HTMLElement[] = [];
  for (const sel of HIDE_SELECTORS) {
    document.querySelectorAll(sel).forEach((el) => {
      const h = el as HTMLElement;
      if (h.style.display !== "none") {
        hidden.push(h);
        h.style.setProperty("visibility", "hidden", "important");
      }
    });
  }
  return hidden;
}

function restoreEditUI(elements: HTMLElement[]): void {
  elements.forEach((el) => el.style.removeProperty("visibility"));
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function makeFilename(prefix: string): string {
  const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
  return `${prefix}-${ts}.png`;
}

/**
 * Filter callback for html-to-image.
 * Returns false for nodes that should be excluded from the capture.
 */
function nodeFilter(node: Node): boolean {
  if (!(node instanceof HTMLElement)) return true;
  if (node.hasAttribute("data-admin-bar")) return false;
  if (node.hasAttribute("data-edit-toolbar")) return false;
  if (node.hasAttribute("data-screenshot-modal")) return false;
  if (node.hasAttribute("data-comment-panel")) return false;
  if (node.hasAttribute("data-block-palette")) return false;
  return true;
}

/* ── public API ── */

/**
 * Viewport capture — captures exactly what the user sees on screen.
 *
 * Targets `document.documentElement` with explicit viewport width/height
 * so that the output is clipped to the visible area only.
 */
export async function captureScreenshot(
  options: ScreenshotOptions = {},
): Promise<void> {
  const {
    filenamePrefix = "screenshot",
    scale = 2,
    hideEditUI: shouldHide = true,
  } = options;

  const element = document.documentElement;
  const hidden = shouldHide ? hideEditUIElements() : [];

  try {
    // Small delay to let visibility:hidden take effect
    await new Promise((r) => setTimeout(r, 80));

    const blob = await toBlob(element, {
      pixelRatio: scale,
      backgroundColor: "#ffffff",
      filter: nodeFilter,
      cacheBust: true,
      // Clip to exactly the viewport dimensions
      width: window.innerWidth,
      height: window.innerHeight,
    });

    if (!blob) throw new Error("Failed to generate image");

    downloadBlob(blob, makeFilename(filenamePrefix));
  } finally {
    restoreEditUI(hidden);
  }
}

/**
 * Full-page capture — captures the entire scrollable page content.
 *
 * Strategy:
 * 1. Save scroll position & styles
 * 2. Scroll through the page to trigger lazy-loaded images
 * 3. Scroll back to top
 * 4. Temporarily expand the document to full scroll height with
 *    overflow:visible so nothing is clipped
 * 5. Capture with full dimensions
 * 6. Restore everything
 */
export async function captureFullPage(
  options: Omit<ScreenshotOptions, "target"> = {},
): Promise<void> {
  const {
    filenamePrefix = "fullpage",
    scale = 2,
    hideEditUI: shouldHide = true,
  } = options;

  const html = document.documentElement;
  const body = document.body;

  // Save original state
  const savedScrollY = window.scrollY;
  const savedScrollX = window.scrollX;
  const savedHtmlOverflow = html.style.overflow;
  const savedHtmlHeight = html.style.height;
  const savedHtmlMaxHeight = html.style.maxHeight;
  const savedBodyOverflow = body.style.overflow;

  const hidden = shouldHide ? hideEditUIElements() : [];

  try {
    // Step 1: Quick scroll-through to trigger lazy-loaded images
    // (IntersectionObserver, loading="lazy", etc.)
    const totalHeight = Math.max(
      body.scrollHeight,
      html.scrollHeight,
      body.offsetHeight,
      html.offsetHeight,
      body.clientHeight,
      html.clientHeight,
    );
    const totalWidth = Math.max(
      body.scrollWidth,
      html.scrollWidth,
      body.offsetWidth,
      html.offsetWidth,
    );

    const step = Math.floor(window.innerHeight * 0.8);
    for (let y = 0; y < totalHeight; y += step) {
      window.scrollTo(0, y);
      await new Promise((r) => setTimeout(r, 50));
    }
    // Scroll to very bottom to trigger any remaining lazy content
    window.scrollTo(0, totalHeight);
    await new Promise((r) => setTimeout(r, 100));

    // Step 2: Scroll back to top
    window.scrollTo(0, 0);
    await new Promise((r) => setTimeout(r, 150));

    // Re-measure after lazy content may have loaded (could change total height)
    const finalHeight = Math.max(
      body.scrollHeight,
      html.scrollHeight,
      body.offsetHeight,
      html.offsetHeight,
    );
    const finalWidth = Math.max(
      body.scrollWidth,
      html.scrollWidth,
      body.offsetWidth,
      html.offsetWidth,
    );

    // Step 3: Temporarily expand the document to full scroll size
    html.style.setProperty("overflow", "visible", "important");
    html.style.setProperty("height", `${finalHeight}px`, "important");
    html.style.setProperty("max-height", "none", "important");
    body.style.setProperty("overflow", "visible", "important");

    // Wait for style changes to apply
    await new Promise((r) => setTimeout(r, 150));

    // Step 4: Capture the full page
    const blob = await toBlob(html, {
      pixelRatio: scale,
      backgroundColor: "#ffffff",
      filter: nodeFilter,
      cacheBust: true,
      width: finalWidth,
      height: finalHeight,
      style: {
        overflow: "visible",
        height: `${finalHeight}px`,
        maxHeight: "none",
      },
    });

    if (!blob) throw new Error("Failed to generate full-page image");

    downloadBlob(blob, makeFilename(filenamePrefix));
  } finally {
    // Step 5: Restore everything
    html.style.overflow = savedHtmlOverflow;
    html.style.height = savedHtmlHeight;
    html.style.maxHeight = savedHtmlMaxHeight;
    body.style.overflow = savedBodyOverflow;
    window.scrollTo(savedScrollX, savedScrollY);
    restoreEditUI(hidden);
  }
}
